import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { API } from '../../utils/api';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, Info } from '@phosphor-icons/react';
import { Checkbox } from '../../components/ui/checkbox';

export default function OnboardingPage() {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    language: language,
    situation: '',
    arrival_time: '',
    province: '',
    can_work: null,
    housing_status: 'unknown',
    stress_areas: [],
    help_preference: 'both'
  });

  const totalSteps = 9;
  const progress = (currentStep / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding`, formData);
      setCurrentStep(9);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Onboarding error:', error);
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.language;
      case 2: return formData.situation;
      case 3: return formData.arrival_time;
      case 4: return formData.province;
      case 5: return formData.situation === 'newcomer' || formData.can_work;
      case 6: return formData.stress_areas.length > 0;
      case 7: return formData.help_preference;
      case 8: return true;
      default: return true;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <LanguageStep formData={formData} setFormData={setFormData} setLanguage={setLanguage} t={t} />;
      case 2:
        return <SituationStep formData={formData} setFormData={setFormData} t={t} />;
      case 3:
        return <ArrivalStep formData={formData} setFormData={setFormData} t={t} />;
      case 4:
        return <ProvinceStep formData={formData} setFormData={setFormData} t={t} />;
      case 5:
        return formData.situation === 'newcomer' ? (
          (() => { handleNext(); return null; })()
        ) : (
          <WorkStep formData={formData} setFormData={setFormData} t={t} />
        );
      case 6:
        return <StressStep formData={formData} setFormData={setFormData} t={t} />;
      case 7:
        return <HelpPreferenceStep formData={formData} setFormData={setFormData} t={t} />;
      case 8:
        return <TrustNoticeStep t={t} language={language} formData={formData} setFormData={setFormData} />;
      case 9:
        return <SuccessStep t={t} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-pp-bg flex flex-col">
      {/* Header - Minimal */}
      <header className="py-5 px-6 bg-white border-b border-pp-border">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-pp-navy rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold">P</span>
            </div>
            <span className="font-heading font-semibold text-lg text-pp-text">PaperPal</span>
          </div>
          {currentStep < 9 && (
            <span className="text-sm text-pp-text-muted">
              {currentStep} / {totalSteps - 1}
            </span>
          )}
        </div>
      </header>

      {/* Progress - Calm, subtle */}
      {currentStep < 9 && (
        <div className="px-6 py-4 bg-white">
          <div className="max-w-xl mx-auto">
            {/* Context line - subtle but visible */}
            <p className="text-xs text-pp-text-muted text-center mb-3">
              {language === 'fr'
                ? 'On utilise vos réponses pour créer votre plan personnalisé (2 min)'
                : 'We\'ll use this to build your personalized plan (takes 2 minutes)'}
            </p>
            <div className="pp-progress">
              <div 
                className="pp-progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Content - Generous spacing, centered */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer - Clean actions */}
      {currentStep < 9 && (
        <footer className="py-6 px-6 bg-white border-t border-pp-border">
          <div className="max-w-xl mx-auto flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
                currentStep === 1 
                  ? 'text-pp-text-muted cursor-not-allowed' 
                  : 'text-pp-text-secondary hover:text-pp-text hover:bg-pp-secondary'
              }`}
              data-testid="onboarding-back"
            >
              <ArrowLeft size={18} />
              {t('onboarding.back')}
            </button>

            {currentStep === 8 ? (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="pp-btn-primary flex items-center gap-2"
                data-testid="onboarding-submit"
              >
                {loading ? t('common.loading') : t('onboarding.submit')}
                <ArrowRight size={18} />
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-8 py-4 rounded-xl font-medium transition-all ${
                  canProceed()
                    ? 'bg-pp-navy text-white hover:bg-pp-navy-hover shadow-sm active:scale-[0.98]'
                    : 'bg-pp-secondary text-pp-text-muted cursor-not-allowed'
                }`}
                data-testid="onboarding-next"
              >
                {t('onboarding.next')}
                <ArrowRight size={18} />
              </button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

// Step components - Calmer, more spacious

function LanguageStep({ formData, setFormData, setLanguage, t }) {
  const options = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' }
  ];

  const handleSelect = (value) => {
    setFormData(prev => ({ ...prev, language: value }));
    setLanguage(value);
  };

  return (
    <div className="text-center">
      <h2 className="pp-heading-2 text-pp-text mb-3" data-testid="onboarding-step-title">
        {t('onboarding.step1.title')}
      </h2>
      <p className="text-pp-text-secondary mb-10">{t('onboarding.step1.subtitle')}</p>
      <div className="grid gap-4">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={`p-6 rounded-2xl border-2 transition-all text-lg font-medium ${
              formData.language === option.value
                ? 'border-pp-navy bg-pp-navy-light text-pp-navy'
                : 'border-pp-border hover:border-pp-navy/30 text-pp-text'
            }`}
            data-testid={`language-option-${option.value}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SituationStep({ formData, setFormData, t }) {
  const options = t('onboarding.step2.options');

  return (
    <div>
      <h2 className="pp-heading-3 text-pp-text mb-10 text-center" data-testid="onboarding-step-title">
        {t('onboarding.step2.title')}
      </h2>
      <div className="grid gap-4">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => setFormData(prev => ({ ...prev, situation: option.value }))}
            className={`p-5 rounded-2xl border-2 text-left transition-all ${
              formData.situation === option.value
                ? 'border-pp-navy bg-pp-navy-light'
                : 'border-pp-border hover:border-pp-navy/30'
            }`}
            data-testid={`situation-option-${option.value}`}
          >
            <span className="font-medium text-pp-text">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ArrivalStep({ formData, setFormData, t }) {
  const options = t('onboarding.step3.options');

  return (
    <div>
      <h2 className="pp-heading-3 text-pp-text mb-3 text-center" data-testid="onboarding-step-title">
        {t('onboarding.step3.title')}
      </h2>
      <p className="text-pp-text-secondary text-center mb-10">{t('onboarding.step3.subtitle')}</p>
      <div className="grid gap-4">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => setFormData(prev => ({ ...prev, arrival_time: option.value }))}
            className={`p-5 rounded-2xl border-2 text-left transition-all ${
              formData.arrival_time === option.value
                ? 'border-pp-navy bg-pp-navy-light'
                : 'border-pp-border hover:border-pp-navy/30'
            }`}
            data-testid={`arrival-option-${option.value}`}
          >
            <span className="font-medium text-pp-text">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ProvinceStep({ formData, setFormData, t }) {
  const options = t('onboarding.step4.options');

  return (
    <div>
      <h2 className="pp-heading-3 text-pp-text mb-3 text-center" data-testid="onboarding-step-title">
        {t('onboarding.step4.title')}
      </h2>
      <p className="text-pp-text-secondary text-center mb-10 leading-relaxed">
        {t('onboarding.step4.subtitle')}
      </p>
      <div className="grid gap-4">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => setFormData(prev => ({ ...prev, province: option.value }))}
            className={`p-5 rounded-2xl border-2 text-left transition-all ${
              formData.province === option.value
                ? 'border-pp-navy bg-pp-navy-light'
                : 'border-pp-border hover:border-pp-navy/30'
            }`}
            data-testid={`province-option-${option.value}`}
          >
            <span className="font-medium text-pp-text">{option.label}</span>
          </button>
        ))}
      </div>
      
      {/* Calm notice */}
      {formData.province === 'other' && (
        <div className="mt-6 pp-disclaimer">
          <p>{t('onboarding.step4.otherNote')}</p>
        </div>
      )}
    </div>
  );
}

function WorkStep({ formData, setFormData, t }) {
  const options = t('onboarding.step5.options');

  return (
    <div>
      <h2 className="pp-heading-3 text-pp-text mb-3 text-center" data-testid="onboarding-step-title">
        {t('onboarding.step5.title')}
      </h2>
      <p className="text-pp-text-secondary text-center mb-10">{t('onboarding.step5.subtitle')}</p>
      <div className="grid gap-4">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => setFormData(prev => ({ ...prev, can_work: option.value }))}
            className={`p-5 rounded-2xl border-2 text-left transition-all ${
              formData.can_work === option.value
                ? 'border-pp-navy bg-pp-navy-light'
                : 'border-pp-border hover:border-pp-navy/30'
            }`}
            data-testid={`work-option-${option.value}`}
          >
            <span className="font-medium text-pp-text">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StressStep({ formData, setFormData, t }) {
  const options = t('onboarding.step6.options');

  const toggleOption = (value) => {
    setFormData(prev => ({
      ...prev,
      stress_areas: prev.stress_areas.includes(value)
        ? prev.stress_areas.filter(v => v !== value)
        : [...prev.stress_areas, value]
    }));
  };

  return (
    <div>
      <h2 className="pp-heading-3 text-pp-text mb-3 text-center" data-testid="onboarding-step-title">
        {t('onboarding.step6.title')}
      </h2>
      <p className="text-pp-text-secondary text-center mb-8">{t('onboarding.step6.subtitle')}</p>
      <div className="grid gap-3">
        {options.map((option) => (
          <div
            key={option.value}
            onClick={() => toggleOption(option.value)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleOption(option.value)}
            className={`flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all cursor-pointer ${
              formData.stress_areas.includes(option.value)
                ? 'border-pp-navy bg-pp-navy-light'
                : 'border-pp-border hover:border-pp-navy/30'
            }`}
            data-testid={`stress-option-${option.value}`}
          >
            <Checkbox 
              checked={formData.stress_areas.includes(option.value)}
              className="pointer-events-none"
            />
            <span className="font-medium text-pp-text">{option.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HelpPreferenceStep({ formData, setFormData, t }) {
  const options = t('onboarding.step7.options');

  return (
    <div>
      <h2 className="pp-heading-3 text-pp-text mb-10 text-center" data-testid="onboarding-step-title">
        {t('onboarding.step7.title')}
      </h2>
      <div className="grid gap-4">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => setFormData(prev => ({ ...prev, help_preference: option.value }))}
            className={`p-5 rounded-2xl border-2 text-left transition-all ${
              formData.help_preference === option.value
                ? 'border-pp-navy bg-pp-navy-light'
                : 'border-pp-border hover:border-pp-navy/30'
            }`}
            data-testid={`help-option-${option.value}`}
          >
            <span className="font-medium text-pp-text">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function TrustNoticeStep({ t, language, formData, setFormData }) {
  return (
    <div className="text-center">
      <div className="w-14 h-14 rounded-2xl bg-pp-warning-muted flex items-center justify-center mx-auto mb-8">
        <Info size={28} className="text-pp-warning" />
      </div>
      <h2 className="pp-heading-3 text-pp-text mb-6" data-testid="onboarding-step-title">
        {t('onboarding.step8.title')}
      </h2>
      <fieldset className="mb-6 text-left" data-testid="housing-status-options">
        <legend className="font-medium mb-3" data-testid="housing-status-question">{language === 'fr' ? 'Avez-vous un logement prévu ?' : 'Do you have housing arranged?'}</legend>
        <p className="text-sm text-pp-text-secondary mb-3" data-testid="housing-status-explanation">{language === 'fr' ? 'Cette réponse nous aide à organiser vos priorités. Vous pouvez passer cette question.' : 'This helps us order your priorities. You can skip this question.'}</p>
        <div className="grid gap-2">
          {[
            ['yes', language === 'fr' ? 'Oui, un logement durable' : 'Yes, settled housing'],
            ['temporary', language === 'fr' ? 'Un hébergement temporaire seulement' : 'Temporary accommodation only'],
            ['no', language === 'fr' ? 'Non, je cherche encore' : 'No, I am still looking'],
            ['unknown', language === 'fr' ? 'Passer cette question' : 'Skip this question'],
          ].map(([value, label]) => <label key={value} className="flex items-center gap-3 rounded-xl border border-pp-border p-3" data-testid={`housing-status-label-${value}`}>
            <input type="radio" name="housing-status" value={value} checked={formData.housing_status === value} onChange={() => setFormData(prev => ({ ...prev, housing_status: value }))} className="focus-ring" data-testid={`housing-status-${value}`} />{label}
          </label>)}
        </div>
      </fieldset>
      {/* Calm disclaimer - amber background */}
      <div className="pp-disclaimer text-left">
        <p>{t('onboarding.step8.notice')}</p>
      </div>
    </div>
  );
}

function SuccessStep({ t }) {
  return (
    <div className="text-center py-8">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="w-20 h-20 rounded-full bg-pp-success-muted flex items-center justify-center mx-auto mb-8"
      >
        <Check size={40} className="text-pp-success" weight="bold" />
      </motion.div>
      <h2 className="pp-heading-3 text-pp-text mb-3" data-testid="onboarding-success-title">
        {t('onboarding.step9.title')}
      </h2>
      <p className="text-pp-text-secondary mb-8">
        {t('onboarding.step9.subtitle')}
      </p>
      <div className="w-8 h-8 border-2 border-pp-navy border-t-transparent rounded-full animate-spin mx-auto" />
    </div>
  );
}
